import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  Button,
  Stack,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const PreviewScreen = ({ open, onClose, questions, jobTitle, position }) => {
  const [showInterview, setShowInterview] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);

  useEffect(() => {
    if (open) {
      
      setShowInterview(false);
      setCurrentIndex(0);
    }
  }, [open, questions, position]);

  const handleStartInterview = () => {
    setShowInterview(true);
    setCurrentIndex(0);
  };

  const handleNextQuestion = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const getIntroVideoUrl = () => {
    return position?.intro_video || position?.introVideo || null;
  };

  const getCloseVideoUrl = () => {
    return position?.close_video || position?.closeVideo || null;
  };

  const getQuestionVideoUrl = (question) => {
    return question?.videoUrl || question?.video_url || null;
  };

  // Enhanced video URL formatter with error handling
  const formatVideoUrl = (url) => {
    if (!url) return null;
    
    // Check if URL is properly formatted
    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }
    
    // If it's a relative URL, prepend the base URL
    if (url.startsWith('/storage/')) {
      return `https://einterview.schoolmgmtsys.com${url}`;
    }
    
    // Handle escaped URLs (like in your API response)
    if (url.includes('\\/')) {
      return url.replace(/\\\//g, '/');
    }
    
    return url;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Stack
        sx={{
          alignItems: "end",
          justifyContent: "end",
          p: 2,
          "&:hover": { cursor: "pointer" },
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </Stack>

      {!showInterview ? (
        <DialogTitle>
          <Grid container spacing={2} p={4}>
            {/* Left: Video */}
            {getIntroVideoUrl() && (
              <Box>
                <video
                  src={formatVideoUrl(getIntroVideoUrl())}
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    borderRadius: "8px",
                  }}
                  controls
                  autoPlay
                  muted
                  onError={(e) => {
                    console.error("Intro video failed to load:", e);
                    console.error("Video URL:", getIntroVideoUrl());
                    console.error("Formatted URL:", formatVideoUrl(getIntroVideoUrl()));
                  }}
                />
              </Box>
            )}

            {/* Right: Content */}
            <Stack alignItems={"center"} p={{ md: 5, xs: 2 }} flex={1}>
              <Stack fontSize={{ md: 25, xs: 20 }} fontWeight={700}>
                Video Interview
              </Stack>
              <Stack fontSize={{ md: 35, xs: 15 }} fontWeight={700}>
                {jobTitle}
              </Stack>
              <Stack fontSize={{ md: 35, xs: 20 }} fontWeight={700}>
                ~ Axcel
              </Stack>
              <Button
                sx={{
                  textTransform: "none",
                  mt: 4,
                  borderRadius: 5,
                  bgcolor: "#1666EB",
                }}
                variant="contained"
                onClick={handleStartInterview}
              >
                Start Interview
              </Button>
            </Stack>
          </Grid>
        </DialogTitle>
      ) : currentIndex !== null && currentIndex < questions.length ? (
        <DialogTitle>
          <Grid container spacing={2} p={4}>
            <Grid
              item
              xs={12}
              md={8}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Box
                sx={{
                  width: "100%",
                  height: { md: "400px", xs: "200px" },
                  backgroundColor: "#fafafa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                }}
              >
                {getQuestionVideoUrl(questions[currentIndex]) ? (
                  <Box>
                    <video
                      src={formatVideoUrl(getQuestionVideoUrl(questions[currentIndex]))}
                      style={{ width: "100%", height: "100%" }}
                      controls
                      autoPlay
                      onError={(e) => {
                        console.error("Question video failed to load:", e);
                        console.error("Video URL:", getQuestionVideoUrl(questions[currentIndex]));
                      }}
                    />
                  </Box>
                ) : (
                  <Typography
                    className="text"
                    fontSize={14}
                    color="text.secondary"
                  >
                    No Video Found. Try to add one in Edit Questions Screen
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={4} display="flex" alignItems="start">
              <Stack alignItems={"start"} width="100%">
                {questions.length > 0 && (
                  <Stack key={questions[currentIndex]?.id} sx={{ mb: 2 }}>
                    <Stack fontSize={{ md: 25, xs: 20 }} fontWeight={700}>
                      Question {currentIndex + 1} of {questions.length}
                    </Stack>
                    <Stack fontSize={{ md: 30, xs: 15 }} fontWeight={700}>
                      {questions[currentIndex]?.question_title}
                    </Stack>
                    <Stack fontSize={{ md: 20, xs: 10 }} fontWeight={700}>
                      {questions[currentIndex]?.full_question}
                    </Stack>
                  </Stack>
                )}

                {currentIndex < questions.length - 1 ? (
                  <Button
                    sx={{ textTransform: "none", mt: 4, borderRadius: 5 }}
                    variant="contained"
                    onClick={handleNextQuestion}
                  >
                    Next Question
                  </Button>
                ) : (
                  <Button
                    sx={{
                      textTransform: "none",
                      mt: 4,
                      borderRadius: 5,
                      bgcolor: "#1666EB",
                    }}
                    variant="contained"
                    onClick={() => setCurrentIndex(null)}
                  >
                    Submit Interview
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogTitle>
      ) : (
        <DialogTitle>
          <Grid container spacing={2} p={4}>
            {/* Left: Video */}
            {getCloseVideoUrl() && (
              <Box>
                <video
                  src={formatVideoUrl(getCloseVideoUrl())}
                  style={{
                    width: "100%",
                    maxWidth: "500px",
                    borderRadius: "8px",
                  }}
                  controls
                  onError={(e) => {
                    console.error("Close video failed to load:", e);
                    console.error("Video URL:", getCloseVideoUrl());
                  }}
                />
              </Box>
            )}

            {/* Right: Content */}
            <Stack alignItems={"center"} p={{ md: 5, xs: 2 }} flex={1}>
              <Stack fontSize={{ xs: 20 }} fontWeight={700}>
                {position?.close_title || "Thanks for Your Time"}
              </Stack>
              <Stack
                fontSize={{ xs: 15 }}
                fontWeight={500}
                textAlign={"center"}
              >
                {position?.close_description || "Thanks for Your Time"}
              </Stack>
              <Stack fontSize={{ xs: 25 }} fontWeight={600}>
                ~ Axcel
              </Stack>
              <Button
                sx={{
                  textTransform: "none",
                  mt: 4,
                  borderRadius: 5,
                  bgcolor: "#1666EB",
                }}
                variant="contained"
                onClick={onClose}
              >
                Finish Interview
              </Button>
            </Stack>
          </Grid>
        </DialogTitle>
      )}
    </Dialog>
  );
};

export default PreviewScreen;